import { FC, useCallback, useMemo, useState } from 'react';
import { CurrencyInputNetwork, CurrencyInputToken } from '../CurrencyInputTypes';
import ModalWrapper from '../../ModalWrapper';
import Typography from '../../Typography';
import Icon from '../../Icon';
import BaseInput from '../../BaseInput';
import NetworkSelectorItem from './NetworkSelectorItem';

import './NetworkSelector.scss';
import { POPULAR_NETWORKS } from '../../../../atoms/bridge/common';

interface NetworkSelectorProps {
  open: boolean;
  networks: CurrencyInputNetwork[];
  title: string;
  selectedToken: CurrencyInputToken;
  onClose: () => void;
  onNetworkSelected: (network: CurrencyInputNetwork) => void;
}

const NetworkSelector: FC<NetworkSelectorProps> = ({
  open,
  networks,
  title,
  selectedToken,
  onClose,
  onNetworkSelected,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchFocus, setSearchFocus] = useState<boolean>(false);

  const onSearchTermChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onFocusSearch = useCallback(() => {
    setSearchFocus(true);
  }, []);

  const onBlurSearch = useCallback(() => {
    setSearchFocus(false);
  }, []);

  const filteredNetworks = useMemo(
    () =>
      networks.filter(network => {
        try {
          if (searchTerm && !network.displayName.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      }),
    [networks, searchTerm],
  );

  const popularNetworks = useMemo(() => {
    return filteredNetworks.filter(network => POPULAR_NETWORKS.includes(network.name));
  }, [filteredNetworks]);

  const otherNetworks = useMemo(() => {
    return filteredNetworks.filter(network => !POPULAR_NETWORKS.includes(network.name));
  }, [filteredNetworks]);

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <div className="nostra__networkSelector">
        <Typography variant="heading-sm" weight="medium">
          {title}
        </Typography>
        <div className={`nostra__networkSelector__input ${searchFocus ? 'nostra__networkSelector__inputFocus' : ''}`}>
          <Icon variant="search" size={16} color="--icon-supporting" />
          <Typography variant="input-body-lg">
            <BaseInput
              value={searchTerm}
              onChange={onSearchTermChange}
              onFocus={onFocusSearch}
              onBlur={onBlurSearch}
              placeholder="Network"
            />
          </Typography>
        </div>
        <div className="nostra__networkSelector__list">
          {popularNetworks.length > 0 && (
            <>
              <Typography
                className="nostra__networkSelector__listHeader"
                variant="table-body-md"
                weight="semi-bold"
                color="text-supporting"
              >
                Popular
              </Typography>
              {popularNetworks.map(network => {
                return (
                  <NetworkSelectorItem
                    network={network}
                    onClick={onNetworkSelected}
                    selectedToken={selectedToken}
                    key={network.name}
                  />
                );
              })}
            </>
          )}
          {otherNetworks.length > 0 && (
            <>
              <Typography
                className="nostra__networkSelector__listHeader"
                variant="table-body-md"
                weight="semi-bold"
                color="text-supporting"
              >
                All networks
              </Typography>
              {otherNetworks.map(network => {
                return (
                  <NetworkSelectorItem
                    network={network}
                    onClick={onNetworkSelected}
                    selectedToken={selectedToken}
                    key={network.name}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};
export default NetworkSelector;
